import React, { useEffect, useState } from 'react';
import Typography from "@mui/material/Typography";
import { useNavigate } from "react-router";
import { useStatus } from "../providers/MsgStatusProvider";
import { Box, CircularProgress } from "@mui/material";
import LinkedInOrgSelectionModal from "../components/LinkedInOrgSelectionModal";

function SocialConnectionLinkedInOrgPage() {
    const navigate = useNavigate();
    const { setStatusMessage, setIsAlertOpen, setStatusType } = useStatus();
    
    // State for organization selection
    const [organizations, setOrganizations] = useState([]);
    const [accessToken, setAccessToken] = useState(null);
    const [showSelectionModal, setShowSelectionModal] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const code = urlParams.get('code');
        const url = process.env.REACT_APP_API_BASE_URL + 'connect/linkedin/callback/';

        if (code) {
            fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Token ${localStorage.getItem('token')}`
                },
                body: JSON.stringify({ 
                    code: code,
                    account_type: 'organization'
                })
            })
                .then(response => response.json())
                .then(data => {
                    if (data.success && data.action_required === 'selection') {
                        // Handle organization selection
                        console.log('LinkedIn organizations found:', data.organizations);
                        const orgs = data.organizations || [];
                        setOrganizations(orgs);
                        setAccessToken(data.access_token || localStorage.getItem('linkedin_org_temp_token'));
                        setIsLoading(false);
                        
                        if (orgs.length === 0) {
                            setStatusMessage('No LinkedIn organizations found. You may need administrator access to organizations, or LinkedIn rate limiting may be affecting the request. Please try again in a few minutes.');
                            setStatusType('warning');
                            setIsAlertOpen(true);
                            setTimeout(() => {
                                navigate('/profile');
                            }, 5000);
                        } else {
                            setShowSelectionModal(true);
                        }
                    } else if (data.success) {
                        // Direct success (backward compatibility)
                        console.log('LinkedIn business account connection successful');
                        setStatusMessage('Successfully connected LinkedIn Organization account');
                        setStatusType('success');
                        setIsAlertOpen(true);
                        
                        // Emit event for UI refresh
                        window.dispatchEvent(new CustomEvent('oauthComplete', {
                            detail: { platform: 'linkedin_org', operation: 'connected' }
                        }));

                        // Redirect to profile page
                        setTimeout(() => {
                            window.location.href = process.env.REACT_APP_BASE_URL + 'profile';
                        }, 2000);
                    } else {
                        throw new Error(data.error || data.message || 'Failed to connect LinkedIn Organization');
                    }
                })
                .catch((error) => {
                    console.error('LinkedIn Organization connection error:', error);
                    setStatusMessage(error.message || 'Failed to connect LinkedIn Organization account');
                    setIsAlertOpen(true);
                    setStatusType('error');
                    setIsLoading(false);

                    // Redirect back to profile page on error
                    setTimeout(() => {
                        navigate('/profile');
                    }, 2000);
                });
        } else {
            // No code parameter
            setIsLoading(false);
            setStatusMessage('Invalid OAuth callback - no authorization code');
            setStatusType('error');
            setIsAlertOpen(true);
            navigate('/profile');
        }
    }, [navigate, setStatusMessage, setIsAlertOpen, setStatusType]);
    
    // Handle organization selection
    const handleOrganizationSelection = async (selectedOrgIds) => {
        if (!selectedOrgIds || selectedOrgIds.length === 0) {
            setStatusMessage('Please select at least one organization');
            setStatusType('warning');
            setIsAlertOpen(true);
            return;
        }
        
        try {
            const response = await fetch(
                process.env.REACT_APP_API_BASE_URL + 'connect/linkedin/organizations/connect/',
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Token ${localStorage.getItem('token')}`
                    },
                    body: JSON.stringify({
                        organization_ids: selectedOrgIds,
                        access_token: accessToken
                    })
                }
            );
            
            const data = await response.json();
            
            if (data.success) {
                setStatusMessage(data.message || 'Successfully connected LinkedIn organizations');
                setStatusType('success');
                setIsAlertOpen(true);
                
                // Emit event for UI refresh
                window.dispatchEvent(new CustomEvent('oauthComplete', {
                    detail: { platform: 'linkedin_org', operation: 'connected' }
                }));
                
                // Close modal and redirect
                setShowSelectionModal(false);
                setTimeout(() => {
                    window.location.href = process.env.REACT_APP_BASE_URL + 'profile';
                }, 2000);
            } else {
                throw new Error(data.error || 'Failed to connect selected organizations');
            }
        } catch (error) {
            console.error('Organization connection error:', error);
            setStatusMessage(error.message || 'Failed to connect organizations');
            setStatusType('error');
            setIsAlertOpen(true);
        }
    };
    
    const handleModalClose = () => {
        setShowSelectionModal(false);
        setStatusMessage('LinkedIn organization connection cancelled');
        setStatusType('info');
        setIsAlertOpen(true);
        navigate('/profile');
    };

    return (
        <Box sx={{ p: 3, textAlign: 'center' }}>
            {isLoading ? (
                <>
                    <CircularProgress size={48} sx={{ mb: 2 }} />
                    <Typography variant="h1">
                        Connecting LinkedIn Organization Account
                    </Typography>
                    <Typography variant="h4">
                        Please wait while we complete the connection...
                    </Typography>
                </>
            ) : (
                <>
                    <Typography variant="h1">
                        LinkedIn Organization Selection
                    </Typography>
                    <Typography variant="h4">
                        Choose which organizations to connect
                    </Typography>
                </>
            )}
            
            {showSelectionModal && (
                <LinkedInOrgSelectionModal
                    open={showSelectionModal}
                    organizations={organizations}
                    onClose={handleModalClose}
                    onSubmit={handleOrganizationSelection}
                />
            )}
        </Box>
    );
}

export default SocialConnectionLinkedInOrgPage;